package co.laiautomation.downtyme

import com.laiautomation.kdxp.KdxpApiClient
import com.laiautomation.kdxp.KdxpConfig
import com.laiautomation.kdxp.repositories.KdxpObjectRepository
import com.laiautomation.kdxp.repositories.KdxpRepositories
import com.laiautomation.kdxp.repositories.KdxpRepositoryPage
import com.laiautomation.kdxp.repositories.KdxpRepositoryQuery
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put

/** One typed page returned by a Downtyme repository. */
data class DowntymePage<T>(
    val items: List<T>,
    val page: Int,
    val pageSize: Int,
    val totalCount: Int?,
    val hasNextPage: Boolean,
)

/** Creates provisioning and typed repositories using KDXP's authenticated transport. */
class DowntymeDataServices(
    config: KdxpConfig,
    private val siteScopeKey: String,
    accessTokenProvider: suspend () -> String? = { config.accessToken },
) {
    private val apiClient = KdxpApiClient(config, accessTokenProvider = accessTokenProvider)
    private val objects = KdxpRepositories(config, apiClient).objects

    val schema = DowntymeSchemaProvisioner(objects)

    suspend fun repositories(
        provisionIfMissing: Boolean = true,
    ): DowntymeCampaignRepositories {
        val bindings = if (provisionIfMissing) schema.provision() else schema.resolveBindings()
        return DowntymeCampaignRepositories(objects, siteScopeKey, bindings)
    }
}

class DowntymeCampaignRepositories internal constructor(
    objects: KdxpObjectRepository,
    scopeKey: String,
    bindings: DowntymeSchemaBindings,
) {
    val characters = DowntymeCharacterRepository(objects, scopeKey, bindings.characters)
    val areas = DowntymeAreaRepository(objects, scopeKey, bindings.areas)
    val actions = DowntymeActionRepository(objects, scopeKey, bindings.actions)
    val resources = DowntymeResourceRepository(objects, scopeKey, bindings.resources)
    val actionLogs = DowntymeActionLogRepository(objects, scopeKey, bindings.actionLogs)
}

abstract class DowntymeRepository<T>(
    private val objects: KdxpObjectRepository,
    private val scopeKey: String,
    private val binding: DowntymeSchemaBinding,
) {
    suspend fun list(
        page: Int = 1,
        pageSize: Int = 20,
        search: String? = null,
        filter: String? = null,
        sort: String? = null,
    ): DowntymePage<T> = objects.listEntries(
        binding.restContextPath,
        KdxpRepositoryQuery(page, pageSize, search, filter, sort),
        scopeKey,
    ).mapPage(::decode)

    suspend fun get(id: Long): T = decode(objects.getEntry(binding.restContextPath, id))

    suspend fun create(value: T): T = decode(
        objects.createEntry(binding.restContextPath, encode(value), scopeKey),
    )

    suspend fun update(id: Long, value: T): T = decode(
        objects.updateEntry(binding.restContextPath, id, encode(value)),
    )

    suspend fun replace(id: Long, value: T): T = decode(
        objects.replaceEntry(binding.restContextPath, id, encode(value)),
    )

    suspend fun delete(id: Long) {
        objects.deleteEntry(binding.restContextPath, id)
    }

    protected abstract fun encode(value: T): JsonObject
    protected abstract fun decode(raw: JsonObject): T
}

class DowntymeCharacterRepository internal constructor(
    objects: KdxpObjectRepository,
    scopeKey: String,
    binding: DowntymeSchemaBinding,
) : DowntymeRepository<DowntymeCharacter>(objects, scopeKey, binding) {
    override fun encode(value: DowntymeCharacter) = buildJsonObject {
        put("name", value.name)
        put("description", value.description)
        putNullable("imageUrl", value.imageUrl)
        putNullable("controllingUserId", value.controllingUserId)
        put("capital", value.capital)
        putNullable(CHARACTER_AREA_FIELD, value.areaId)
    }

    override fun decode(raw: JsonObject) = DowntymeCharacter(
        id = raw.long("id"),
        name = raw.text("name"),
        description = raw.text("description"),
        imageUrl = raw.optionalText("imageUrl"),
        controllingUserId = raw.long("controllingUserId"),
        areaId = raw.long(CHARACTER_AREA_FIELD),
        capital = raw.int("capital"),
    )
}

class DowntymeAreaRepository internal constructor(
    objects: KdxpObjectRepository,
    scopeKey: String,
    binding: DowntymeSchemaBinding,
) : DowntymeRepository<DowntymeArea>(objects, scopeKey, binding) {
    override fun encode(value: DowntymeArea) = buildJsonObject {
        put("name", value.name)
        put("description", value.description)
        putNullable("imageUrl", value.imageUrl)
    }

    override fun decode(raw: JsonObject) = DowntymeArea(
        id = raw.long("id"),
        name = raw.text("name"),
        description = raw.text("description"),
        imageUrl = raw.optionalText("imageUrl"),
    )
}

class DowntymeActionRepository internal constructor(
    objects: KdxpObjectRepository,
    scopeKey: String,
    binding: DowntymeSchemaBinding,
) : DowntymeRepository<DowntymeAction>(objects, scopeKey, binding) {
    override fun encode(value: DowntymeAction) = buildJsonObject {
        put("name", value.name)
        put("description", value.description)
        putNullable("imageUrl", value.imageUrl)
        put("cost", value.cost)
        putNullable(ACTION_AREA_FIELD, value.areaId)
        putNullable(ACTION_RESOURCE_FIELD, value.resourceTypeId)
    }

    override fun decode(raw: JsonObject) = DowntymeAction(
        id = raw.long("id"),
        name = raw.text("name"),
        description = raw.text("description"),
        imageUrl = raw.optionalText("imageUrl"),
        resourceTypeId = raw.long(ACTION_RESOURCE_FIELD),
        cost = raw.int("cost"),
        areaId = raw.long(ACTION_AREA_FIELD),
    )
}

class DowntymeResourceRepository internal constructor(
    objects: KdxpObjectRepository,
    scopeKey: String,
    binding: DowntymeSchemaBinding,
) : DowntymeRepository<DowntymeResourceType>(objects, scopeKey, binding) {
    override fun encode(value: DowntymeResourceType) = buildJsonObject {
        put("name", value.name)
        put("description", value.description)
        put("iconKey", value.iconKey)
    }

    override fun decode(raw: JsonObject) = DowntymeResourceType(
        id = raw.long("id"),
        name = raw.text("name"),
        description = raw.text("description"),
        iconKey = raw.text("iconKey").ifBlank { "coins" },
    )
}

class DowntymeActionLogRepository internal constructor(
    objects: KdxpObjectRepository,
    scopeKey: String,
    binding: DowntymeSchemaBinding,
) : DowntymeRepository<DowntymeActionLog>(objects, scopeKey, binding) {
    override fun encode(value: DowntymeActionLog) = buildJsonObject {
        put(LOG_CHARACTER_FIELD, value.characterId)
        put(LOG_ACTION_FIELD, value.actionId)
        put("submittedByUserId", value.submittedByUserId)
        putNullable(LOG_RESOURCE_FIELD, value.resourceTypeId)
        put("amountSpent", value.amountSpent)
        put("notes", value.notes)
        put("createdAtEpochMillis", value.createdAtEpochMillis)
    }

    override fun decode(raw: JsonObject) = DowntymeActionLog(
        id = raw.long("id"),
        characterId = requireNotNull(raw.long(LOG_CHARACTER_FIELD)) { "Action log is missing character." },
        actionId = requireNotNull(raw.long(LOG_ACTION_FIELD)) { "Action log is missing action." },
        submittedByUserId = requireNotNull(raw.long("submittedByUserId")) {
            "Action log is missing submitting user."
        },
        resourceTypeId = raw.long(LOG_RESOURCE_FIELD),
        amountSpent = raw.int("amountSpent"),
        notes = raw.text("notes"),
        createdAtEpochMillis = requireNotNull(raw.long("createdAtEpochMillis")) {
            "Action log is missing creation time."
        },
    )
}

private fun <T> KdxpRepositoryPage.mapPage(mapper: (JsonObject) -> T) = DowntymePage(
    items = items.map(mapper),
    page = page,
    pageSize = pageSize,
    totalCount = totalCount,
    hasNextPage = hasNextPage,
)

private fun JsonObject.text(key: String): String =
    (this[key] as? JsonPrimitive)?.contentOrNull.orEmpty()

private fun JsonObject.optionalText(key: String): String? = text(key).takeIf(String::isNotBlank)

private fun JsonObject.long(key: String): Long? = when (val value = this[key]) {
    is JsonPrimitive -> value.longOrNull ?: value.contentOrNull?.toLongOrNull()
    else -> null
}

private fun JsonObject.int(key: String): Int = long(key)?.toInt() ?: 0

private fun kotlinx.serialization.json.JsonObjectBuilder.putNullable(key: String, value: String?) {
    put(key, value?.let(::JsonPrimitive) ?: JsonNull)
}

private fun kotlinx.serialization.json.JsonObjectBuilder.putNullable(key: String, value: Long?) {
    put(key, value?.let(::JsonPrimitive) ?: JsonNull)
}

private const val CHARACTER_AREA_FIELD = "r_${DowntymeObjectKeys.AREA_CHARACTERS}_c_${DowntymeObjectKeys.AREA}Id"
private const val ACTION_AREA_FIELD = "r_${DowntymeObjectKeys.AREA_ACTIONS}_c_${DowntymeObjectKeys.AREA}Id"
private const val ACTION_RESOURCE_FIELD = "r_${DowntymeObjectKeys.RESOURCE_ACTIONS}_c_${DowntymeObjectKeys.RESOURCE}Id"
private const val LOG_CHARACTER_FIELD = "r_${DowntymeObjectKeys.CHARACTER_LOGS}_c_${DowntymeObjectKeys.CHARACTER}Id"
private const val LOG_ACTION_FIELD = "r_${DowntymeObjectKeys.ACTION_LOGS}_c_${DowntymeObjectKeys.ACTION}Id"
private const val LOG_RESOURCE_FIELD = "r_${DowntymeObjectKeys.RESOURCE_LOGS}_c_${DowntymeObjectKeys.RESOURCE}Id"
